import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  MessageSquare,
  Sparkles,
  Headphones,
  Zap,
  Wallet,
  Trophy,
  RefreshCw,
  Fingerprint,
} from "lucide-react";
import Logo from "@/components/seven/Logo";
import CursorFX from "@/components/seven/CursorFX";
import Reveal from "@/components/seven/Reveal";
import Marquee from "@/components/seven/Marquee";
import Lightbox from "@/components/seven/Lightbox";
import ClientLogos from "@/components/seven/ClientLogos";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const WHATSAPP_URL = "https://wa.me/5561981105746";
const HERO_VIDEO = "/videos/reels-hero.mp4";
const CLONE_VIDEO = "/videos/clone-ia.mp4";
const PROJECT_VIDEO_1 = "/videos/project-1.mp4";
const PROJECT_VIDEO_2 = "/videos/project-2.mp4";
const PROJECT_VIDEO_3 = "/videos/project-3.mp4";
const PROJECT_VIDEO_4 = "/videos/project-4.mp4";

/* -------------------- Floating top bar -------------------- */
const TopBar = () => (
  <header
    className="fixed top-3 sm:top-5 inset-x-0 z-40 flex justify-center px-3 sm:px-6"
    data-testid="portfolio-top-bar"
  >
    <div className="w-full max-w-6xl flex items-center justify-between rounded-full border border-[rgba(197,212,181,0.14)] bg-[rgba(10,10,10,0.65)] backdrop-blur-xl px-3.5 sm:px-5 py-2.5">
      <Link to="/" data-testid="back-to-home" className="flex items-center gap-2">
        <ArrowLeft size={14} className="text-[#F5F1E8]/70" />
        <Logo size={22} />
      </Link>
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="group inline-flex items-center gap-2 rounded-full bg-[#C5D4B5] text-[#0A0A0A] px-3.5 sm:px-4 py-2 text-[12.5px] sm:text-[13px] font-medium hover:brightness-95 transition-all"
        data-testid="portfolio-whatsapp-btn"
        data-cursor="hover"
      >
        <MessageSquare size={14} strokeWidth={2.4} />
        <span className="hidden sm:inline">Quero vender mais</span>
        <span className="sm:hidden">Vender mais</span>
        <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
      </a>
    </div>
  </header>
);

/* -------------------- Hero with background video -------------------- */
const Hero = () => (
  <section
    className="relative min-h-[100svh] w-full overflow-hidden flex items-center bg-radial-hero bg-grid"
    data-testid="portfolio-hero"
  >
    <div className="aurora" aria-hidden="true" />
    <video
      src={HERO_VIDEO}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      className="absolute inset-0 w-full h-full object-cover opacity-70"
      data-testid="hero-bg-video"
      onError={(e) => {
        // Hide broken video; let the gradient + aurora act as background
        e.currentTarget.style.display = "none";
      }}
    />
    {/* Dark overlay */}
    <div
      className="absolute inset-0"
      style={{
        background:
          "linear-gradient(180deg, rgba(10,10,10,0.35) 0%, rgba(10,10,10,0.5) 50%, rgba(10,10,10,0.88) 100%)",
      }}
    />
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(60% 50% at 20% 80%, rgba(197,212,181,0.12), transparent 70%)",
      }}
    />

    <div className="relative max-w-6xl mx-auto px-5 sm:px-8 w-full pt-28 sm:pt-32 pb-20">
      <Reveal>
        <span
          className="mono inline-flex items-center gap-2 text-[10.5px] tracking-[0.24em] uppercase text-[#C5D4B5] px-3 py-1.5 rounded-full border border-[rgba(197,212,181,0.35)] bg-[rgba(197,212,181,0.05)] backdrop-blur"
          style={{
            boxShadow:
              "0 0 0 1px rgba(197,212,181,0.05), 0 0 40px -10px rgba(197,212,181,0.25)",
          }}
          data-testid="hero-badge"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#C5D4B5] pulse-dot" />
          Apenas 5 vagas este mês
        </span>
      </Reveal>

      <Reveal delay={160}>
        <h1 className="mt-7 text-[40px] sm:text-6xl lg:text-[84px] leading-[0.98] tracking-[-0.025em] font-medium text-[#F5F1E8] max-w-5xl">
          Sua marca merece <br className="hidden sm:block" />
          mais do que <span className="text-[#C5D4B5]">marketing comum.</span>
        </h1>
      </Reveal>

      <Reveal delay={300}>
        <p className="mt-7 max-w-2xl text-[15px] sm:text-[17px] leading-relaxed text-[#F5F1E8]/75">
          Na Seven Impulse, cada criativo, campanha, site e automação nasce com
          um objetivo claro: gerar resultado para negócios que querem crescer
          com inteligência.
        </p>
      </Reveal>

      <Reveal delay={440}>
        <div className="mt-10 flex flex-wrap items-center gap-3">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 rounded-full bg-[#C5D4B5] text-[#0A0A0A] px-5 py-3 text-[14px] font-medium hover:brightness-95 transition sage-glow-soft"
            data-testid="hero-cta"
            data-cursor="hover"
          >
            Agendar conversa
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="#cases"
            className="group inline-flex items-center gap-2 rounded-full border border-[rgba(245,241,232,0.25)] text-[#F5F1E8] px-5 py-3 text-[14px] font-medium hover:border-[#C5D4B5] hover:text-[#C5D4B5] transition"
            data-cursor="hover"
          >
            Ver projetos
            <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        </div>
      </Reveal>

      <Reveal delay={580}>
        <div className="mt-16 sm:mt-24 flex items-center gap-3 text-[#F5F1E8]/40">
          <span className="h-px w-10 bg-[#F5F1E8]/30" />
          <span className="mono text-[10.5px] tracking-[0.22em] uppercase">Role para descobrir</span>
        </div>
      </Reveal>
    </div>
  </section>
);

/* -------------------- Process -------------------- */
const steps = [
  {
    n: "01",
    title: "Diagnóstico",
    desc:
      "Entendemos seu negócio, seu mercado, sua operação e seus objetivos antes de qualquer execução.",
  },
  {
    n: "02",
    title: "Estratégia com IA",
    desc:
      "Unimos dados, criatividade e inteligência artificial para construir um plano com direção, velocidade e performance.",
  },
  {
    n: "03",
    title: "Execução e escala",
    desc:
      "Criamos, publicamos, medimos e otimizamos em tempo real para transformar estratégia em resultado.",
  },
];

const Process = () => (
  <section
    className="relative py-28 sm:py-40 border-t border-[rgba(245,241,232,0.06)]"
    data-testid="process-section"
  >
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <Reveal>
        <span className="mono text-[11px] tracking-[0.24em] uppercase text-[#C5D4B5]">
          / Como trabalhamos
        </span>
      </Reveal>
      <Reveal delay={80}>
        <h2 className="mt-5 text-[34px] sm:text-5xl lg:text-[58px] leading-[1.02] tracking-[-0.02em] font-medium text-[#F5F1E8] max-w-4xl">
          Três passos. <span className="text-[#C5D4B5]">Zero achismo.</span>
        </h2>
      </Reveal>

      <div className="mt-14 sm:mt-20 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
        {steps.map((s, i) => (
          <Reveal key={s.n} delay={i * 110}>
            <div
              className="group relative h-full rounded-2xl border border-[rgba(245,241,232,0.08)] bg-[#0E0E0E] p-8 sm:p-9 hover:border-[rgba(197,212,181,0.4)] hover:-translate-y-1 transition-all duration-700"
              style={{ transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)" }}
              data-cursor="hover"
            >
              <div className="flex items-center justify-between mb-12">
                <span className="mono text-[12px] tracking-[0.24em] uppercase text-[#C5D4B5]">
                  Passo {s.n}
                </span>
                <span className="text-[#F5F1E8]/15 text-[64px] font-medium tracking-[-0.05em] leading-none mono">
                  {s.n}
                </span>
              </div>
              <h3 className="text-[24px] sm:text-[28px] font-medium tracking-tight text-[#F5F1E8] mb-4">
                {s.title}
              </h3>
              <p className="text-[14.5px] leading-relaxed text-[#F5F1E8]/65">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={400}>
        <p className="mt-16 sm:mt-20 max-w-3xl text-[20px] sm:text-[26px] leading-[1.35] tracking-[-0.01em] text-[#F5F1E8]/80">
          Empresa não precisa apenas de posts bonitos.{" "}
          <span className="text-[#C5D4B5]">
            Empresa precisa de vendas, posicionamento e clareza para crescer.
          </span>
        </p>
      </Reveal>
    </div>
  </section>
);

/* -------------------- Cases gallery (horizontal scroll) -------------------- */
const galleryItems = [
  { type: "image", src: "/images/case-aria.png", title: "Identidade visual · Espaço Ária", tag: "BRANDING / FISIOTERAPIA" },
  { type: "image", src: "/images/case-amine.png", title: "Amine Festas · landing page", tag: "WEB / DESIGN" },
  { type: "video", src: HERO_VIDEO, title: "Reels institucional Seven Impulse", tag: "VÍDEO / REELS" },
  { type: "video", src: PROJECT_VIDEO_1, title: "Campanha em vídeo", tag: "REELS / CRIATIVO" },
  { type: "video", src: PROJECT_VIDEO_2, title: "Projeto cliente", tag: "PRODUÇÃO" },
  { type: "video", src: CLONE_VIDEO, title: "Clone digital com IA", tag: "AVATAR / IA" },
  { type: "video", src: PROJECT_VIDEO_3, title: "Conteúdo orgânico", tag: "INSTAGRAM" },
  { type: "video", src: PROJECT_VIDEO_4, title: "Caixa revelação", tag: "UGC / EVENTO" },
];

const Gallery = () => {
  const trackRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const [lbIndex, setLbIndex] = useState(null);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let raf;
    const speed = 0.55; // px per frame
    const step = () => {
      if (!paused && lbIndex === null) {
        el.scrollLeft += speed;
        if (el.scrollLeft >= el.scrollWidth / 2) {
          el.scrollLeft = 0;
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [paused, lbIndex]);

  const doubled = [...galleryItems, ...galleryItems];

  return (
    <section id="cases" className="relative py-28 sm:py-40 border-t border-[rgba(245,241,232,0.06)]" data-testid="gallery-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <Reveal>
          <span className="mono text-[11px] tracking-[0.24em] uppercase text-[#C5D4B5]">
            / Galeria
          </span>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="mt-5 text-[34px] sm:text-5xl lg:text-[58px] leading-[1.02] tracking-[-0.02em] font-medium text-[#F5F1E8] max-w-4xl">
            Projetos que unem <span className="text-[#C5D4B5]">estética, estratégia e performance.</span>
          </h2>
        </Reveal>
        <Reveal delay={160}>
          <p className="mt-6 max-w-2xl text-[15px] sm:text-[17px] leading-relaxed text-[#F5F1E8]/65">
            Uma amostra dos conteúdos, campanhas, sites, criativos e soluções
            que desenvolvemos para marcas que querem crescer com mais
            inteligência. Clique em qualquer item para expandir.
          </p>
        </Reveal>
      </div>

      <div
        ref={trackRef}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
        className="mt-14 sm:mt-20 overflow-x-auto flex gap-4 sm:gap-5 px-5 sm:px-8 pb-3 scroll-smooth"
        style={{ scrollbarWidth: "none" }}
        data-testid="gallery-track"
      >
        {doubled.map((it, i) => {
          const heights = ["h-[320px]", "h-[400px]", "h-[280px]", "h-[360px]"];
          const widths = ["w-[260px]", "w-[320px]", "w-[240px]", "w-[300px]"];
          const h = heights[i % heights.length];
          const w = widths[i % widths.length];
          const realIndex = i % galleryItems.length;
          return (
            <button
              type="button"
              key={`${it.title}-${i}`}
              onClick={() => setLbIndex(realIndex)}
              className={`group relative shrink-0 ${w} ${h} rounded-2xl overflow-hidden border border-[rgba(245,241,232,0.08)] bg-[#0E0E0E] hover:border-[rgba(197,212,181,0.45)] transition-all duration-700 text-left`}
              style={{ transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)" }}
              data-cursor="hover"
              data-testid={`gallery-item-${realIndex}`}
              aria-label={`Abrir ${it.title}`}
            >
              {it.type === "video" ? (
                <video
                  src={it.src}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                />
              ) : (
                <img
                  src={it.src}
                  alt={it.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105 grayscale-[15%] group-hover:grayscale-0"
                />
              )}
              <div
                className="absolute inset-0 transition-opacity duration-500 opacity-60 group-hover:opacity-80"
                style={{
                  background:
                    "linear-gradient(180deg, transparent 30%, rgba(10,10,10,0.95) 100%)",
                }}
              />
              <div className="absolute inset-x-0 bottom-0 p-5">
                <span className="mono text-[10px] tracking-[0.22em] uppercase text-[#C5D4B5]">
                  {it.tag}
                </span>
                <p className="mt-1.5 text-[15px] text-[#F5F1E8] font-medium tracking-tight">
                  {it.title}
                </p>
              </div>
              <span className="absolute top-3 right-3 h-7 w-7 rounded-full bg-[rgba(10,10,10,0.7)] backdrop-blur flex items-center justify-center text-[#C5D4B5] opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-[11px] mono">
                +
              </span>
            </button>
          );
        })}
      </div>

      <Lightbox
        items={galleryItems}
        index={lbIndex}
        onClose={() => setLbIndex(null)}
        onNav={(n) => setLbIndex(n)}
      />
    </section>
  );
};

/* -------------------- Why love us -------------------- */
const reasons = [
  { icon: Headphones, title: "Suporte sob demanda", desc: "Solicite em qualquer tempo do dia. Você fala. A gente resolve." },
  { icon: Zap, title: "Entrega ultrarrápida", desc: "Receba toda a estratégia e execução em apenas alguns dias." },
  { icon: Wallet, title: "Taxa mensal fixa", desc: "Sem surpresas. Pague o mesmo preço fixo todos os meses." },
  { icon: Trophy, title: "Projetos premiados", desc: "Impressione seus clientes com criativos e ofertas eficientes." },
  { icon: RefreshCw, title: "Revisões ilimitadas", desc: "Revise seus projetos até ficar 100% satisfeito. Sem limites." },
  { icon: Fingerprint, title: "Único e totalmente seu", desc: "Todos os seus projetos são criados especialmente para você." },
];

const Reasons = () => (
  <section
    className="relative py-28 sm:py-40 border-t border-[rgba(245,241,232,0.06)]"
    data-testid="reasons-section"
  >
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <Reveal>
        <span className="mono text-[11px] tracking-[0.24em] uppercase text-[#C5D4B5]">
          / Por que escolher
        </span>
      </Reveal>
      <Reveal delay={80}>
        <h2 className="mt-5 text-[34px] sm:text-5xl lg:text-[58px] leading-[1.02] tracking-[-0.02em] font-medium text-[#F5F1E8] max-w-4xl">
          Motivos pelos quais <span className="text-[#C5D4B5]">você vai nos amar.</span>
        </h2>
      </Reveal>
      <Reveal delay={160}>
        <p className="mt-6 max-w-2xl text-[15px] sm:text-[17px] leading-relaxed text-[#F5F1E8]/65">
          Depois de conectar com a Seven Impulse, você nunca mais vai querer
          outro serviço de marketing. Sério.
        </p>
      </Reveal>

      <div className="mt-14 sm:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {reasons.map((r, i) => (
          <Reveal key={r.title} delay={i * 90}>
            <div
              className="group h-full rounded-2xl border border-[rgba(245,241,232,0.08)] bg-[#0E0E0E] p-7 sm:p-8 hover:border-[rgba(197,212,181,0.4)] hover:-translate-y-1 transition-all duration-700"
              style={{ transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)" }}
              data-cursor="hover"
            >
              <div className="h-10 w-10 rounded-xl bg-[rgba(197,212,181,0.08)] border border-[rgba(197,212,181,0.18)] flex items-center justify-center text-[#C5D4B5] mb-7">
                <r.icon size={18} strokeWidth={1.6} />
              </div>
              <h3 className="text-[20px] font-medium tracking-tight text-[#F5F1E8] mb-3">
                {r.title}
              </h3>
              <p className="text-[14px] leading-relaxed text-[#F5F1E8]/65">{r.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* -------------------- Big marquee -------------------- */
const SolutionMarquee = () => (
  <section
    className="relative py-28 sm:py-36 border-t border-[rgba(245,241,232,0.06)] overflow-hidden"
    data-testid="solution-marquee"
  >
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <Reveal>
        <span className="mono text-[11px] tracking-[0.24em] uppercase text-[#C5D4B5]">
          / Solução
        </span>
      </Reveal>
      <Reveal delay={80}>
        <h2 className="mt-5 text-[34px] sm:text-5xl lg:text-[58px] leading-[1.02] tracking-[-0.02em] font-medium text-[#F5F1E8] max-w-4xl">
          Todas as suas necessidades de <span className="text-[#C5D4B5]">marketing.</span>
        </h2>
      </Reveal>
      <Reveal delay={160}>
        <p className="mt-6 max-w-2xl text-[15px] sm:text-[17px] leading-relaxed text-[#F5F1E8]/65">
          Gerir um negócio de sucesso vai muito além de presença digital. É por
          isso que tratamos de todas as suas necessidades, para que você não
          tenha de procurar mais ninguém.
        </p>
      </Reveal>
    </div>

    <div className="mt-16 border-y border-[rgba(197,212,181,0.1)] py-6 bg-[rgba(197,212,181,0.02)]">
      <BigMarquee
        items={[
          "Filmmaker",
          "Design & Branding",
          "Soluções com IA",
          "Gestão de Tráfego",
          "Identidade Visual",
          "Estratégia Digital",
          "Conteúdo Criativo",
          "Sites de Alta Conversão",
          "Google Business",
          "Automações com IA",
          "Campanhas de Performance",
          "Criativos para Anúncios",
        ]}
      />
    </div>
  </section>
);

const BigMarquee = ({ items }) => {
  const [paused, setPaused] = useState(false);
  const list = [...items, ...items];
  return (
    <div
      className="overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        className="marquee-track"
        style={{
          animationPlayState: paused ? "paused" : "running",
          animationDuration: "55s",
        }}
      >
        {list.map((it, i) => (
          <span
            key={i}
            className="text-[32px] sm:text-[48px] lg:text-[64px] tracking-[-0.02em] font-medium text-[#F5F1E8]/85 px-8 sm:px-12 flex items-center gap-10 whitespace-nowrap"
          >
            {it}
            <span className="text-[#C5D4B5] text-[24px]">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
};

/* -------------------- FAQ -------------------- */
const faqs = [
  {
    q: "A Seven Impulse atende qualquer tipo de empresa?",
    a: "Atendemos principalmente negócios locais e empresas prestadoras de serviço que precisam de mais presença, posicionamento e vendas.",
  },
  {
    q: "Vocês fazem apenas posts para Instagram?",
    a: "Não. Nosso foco é estratégia completa: conteúdo, tráfego, sites, IA, automação, Google e performance.",
  },
  {
    q: "Em quanto tempo começo a ver resultado?",
    a: "Depende do estágio da empresa, mas o objetivo é gerar os primeiros sinais de movimento nos primeiros 30 dias.",
  },
  {
    q: "Vocês também criam site e landing page?",
    a: "Sim. Criamos sites e páginas modernas com foco em posicionamento, conversão e captação de oportunidades.",
  },
  {
    q: "A IA substitui o trabalho humano?",
    a: "Não. A IA acelera processos, melhora análise e aumenta produtividade, mas a estratégia e a direção criativa continuam sendo humanas.",
  },
];

const FAQ = () => (
  <section className="relative py-28 sm:py-40 border-t border-[rgba(245,241,232,0.06)]" data-testid="faq-section">
    <div className="max-w-4xl mx-auto px-5 sm:px-8">
      <Reveal>
        <span className="mono text-[11px] tracking-[0.24em] uppercase text-[#C5D4B5]">
          / Perguntas frequentes
        </span>
      </Reveal>
      <Reveal delay={80}>
        <h2 className="mt-5 text-[34px] sm:text-5xl lg:text-[58px] leading-[1.02] tracking-[-0.02em] font-medium text-[#F5F1E8]">
          Dúvidas? <span className="text-[#C5D4B5]">A gente responde.</span>
        </h2>
      </Reveal>

      <Reveal delay={160}>
        <Accordion type="single" collapsible className="mt-12 sm:mt-16 w-full">
          {faqs.map((item, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="border-b border-[rgba(245,241,232,0.08)]"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger
                className="text-[16px] sm:text-[19px] py-6 text-left font-medium text-[#F5F1E8] hover:text-[#C5D4B5] hover:no-underline transition-colors"
                data-cursor="hover"
              >
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-[14.5px] leading-relaxed text-[#F5F1E8]/70 pb-6 pr-8">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

/* -------------------- Final CTA with video background -------------------- */
const FinalCTA = () => (
  <section className="relative py-28 sm:py-40 border-t border-[rgba(245,241,232,0.06)] overflow-hidden" data-testid="portfolio-cta">
    <video
      src={HERO_VIDEO}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      className="absolute inset-0 w-full h-full object-cover opacity-30"
    />
    <div
      className="absolute inset-0"
      style={{
        background:
          "linear-gradient(180deg, rgba(10,10,10,0.85) 0%, rgba(10,10,10,0.7) 50%, rgba(10,10,10,0.95) 100%)",
      }}
    />

    <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
      <Reveal>
        <div
          className="relative inline-block rounded-3xl border border-[rgba(197,212,181,0.18)] bg-[rgba(14,14,14,0.65)] backdrop-blur-xl p-10 sm:p-16 sage-glow-soft"
          style={{
            animation: "fadeUp 900ms cubic-bezier(0.22, 1, 0.36, 1) both",
          }}
        >
          <span className="mono inline-flex items-center gap-2 text-[10.5px] tracking-[0.24em] uppercase text-[#C5D4B5] px-3 py-1.5 rounded-full border border-[rgba(197,212,181,0.35)] bg-[rgba(197,212,181,0.05)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#C5D4B5] pulse-dot" />
            Apenas 5 vagas este mês
          </span>

          <h2 className="mt-7 text-[32px] sm:text-5xl lg:text-[60px] leading-[1.04] tracking-[-0.025em] font-medium text-[#F5F1E8]">
            Pronto para escalar o seu negócio com{" "}
            <span className="text-[#C5D4B5]">estratégia, criatividade e IA?</span>
          </h2>
          <p className="mt-7 text-[15px] sm:text-[17px] leading-relaxed text-[#F5F1E8]/75 max-w-2xl mx-auto">
            Vamos entender o seu momento, identificar oportunidades e mostrar
            como a Seven Impulse pode ajudar sua marca a crescer com mais
            clareza e performance.
          </p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-9 group inline-flex items-center gap-2 rounded-full bg-[#C5D4B5] text-[#0A0A0A] px-6 py-3.5 text-[15px] font-medium hover:brightness-95 transition sage-glow"
            data-testid="final-cta-btn"
            data-cursor="hover"
          >
            Agendar conversa
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

/* -------------------- Footer -------------------- */
const Footer = () => (
  <footer className="relative border-t border-[rgba(245,241,232,0.06)] py-16 bg-[#080808]" data-testid="portfolio-footer">
    <div className="max-w-6xl mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
      <div className="md:col-span-7">
        <Logo size={36} />
        <p className="mt-5 max-w-md text-[14px] text-[#F5F1E8]/55 leading-relaxed">
          Criatividade, performance e inteligência artificial para negócios que
          querem crescer. Brasília–DF. Clientes em todo o Brasil.
        </p>
      </div>
      <div className="md:col-span-5 flex md:justify-end">
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-full border border-[rgba(245,241,232,0.18)] text-[#F5F1E8] px-5 py-3 text-[14px] font-medium hover:border-[#C5D4B5] hover:text-[#C5D4B5] transition w-fit"
          data-cursor="hover"
          data-testid="footer-back-home"
        >
          <ArrowLeft size={15} />
          Voltar para a home
        </Link>
      </div>
    </div>
    <div className="max-w-6xl mx-auto px-5 sm:px-8 mt-14 pt-6 border-t border-[rgba(197,212,181,0.08)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
      <p className="mono text-[10.5px] tracking-[0.22em] uppercase text-[#F5F1E8]/45">
        Portfólio · Seven Impulse
      </p>
      <p className="mono text-[10.5px] tracking-[0.18em] uppercase text-[#F5F1E8]/45">
        © {new Date().getFullYear()} Seven Impulse
      </p>
    </div>
  </footer>
);

/* -------------------- Page -------------------- */
export default function Portfolio() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Portfólio · Seven Impulse";
    return () => {
      document.title = prev;
    };
  }, []);
  return (
    <div className="relative min-h-screen bg-[#0A0A0A] text-[#F5F1E8]" data-testid="portfolio-page">
      <CursorFX />
      <TopBar />
      <Hero />
      <Process />
      <Gallery />
      <ClientLogos />
      <Reasons />
      <SolutionMarquee />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}
